import { resolveToolUiResourceUri } from "../widget-runtime/tool-ui-resource.js";

/**
 * Narrow, host-compat-specific bridge detection from a tool's `_meta`.
 *
 * This is NOT canonical widget detection — it deliberately classifies only the
 * `_meta`-declared bridge (MCP Apps `ui.resourceUri` and/or OpenAI Apps
 * `openai/outputTemplate`), which is all the compat engine needs to bucket a
 * tool. It does NOT scan tool *results* for inline `ui://` resources; the
 * canonical detector that does lives in `@mcpjam/widget-react`. Named narrowly
 * so it can't be mistaken for general detection.
 */

/** The widget bridge a tool declares in its `_meta`. */
export enum HostCompatBridge {
  /** MCP Apps only (`_meta.ui.resourceUri`). */
  MCP_APPS = "mcp-apps",
  /** OpenAI Apps only (`openai/outputTemplate`). */
  OPENAI_SDK = "openai-sdk",
  /** Declares both bridges. */
  OPENAI_SDK_AND_MCP_APPS = "openai-sdk-and-mcp-apps",
}

export function detectHostCompatBridgeFromMeta(
  toolMeta: Record<string, unknown> | undefined,
): HostCompatBridge | null {
  // Require a real template string — `Boolean({})` would otherwise bucket
  // malformed metadata (e.g. `openai/outputTemplate: {}`) as a widget.
  const template = toolMeta?.["openai/outputTemplate"];
  const hasOpenAi = typeof template === "string" && template.length > 0;
  const hasMcpApps = Boolean(resolveToolUiResourceUri(toolMeta));
  if (hasOpenAi && hasMcpApps) return HostCompatBridge.OPENAI_SDK_AND_MCP_APPS;
  if (hasOpenAi) return HostCompatBridge.OPENAI_SDK;
  if (hasMcpApps) return HostCompatBridge.MCP_APPS;
  return null;
}
